import React, { useState } from "react";
import { CheckCircle, X, ExternalLink } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';

function EligibilityCardsSection() {
  const { language } = useLanguage();
  const [openModal, setOpenModal] = useState(null);

  const content = {
    en: {
      cards: [
        {
          id: 'who-can-apply',
          title: 'Who Can Apply',
          content: "Any group of individuals with a common economic or social objective may apply to form a cooperative society. The proposed society should function on cooperative principles and serve the collective interest of its members."
        },
        {
          id: 'eligibility',
          title: 'Types of Societies Eligible for Registration',
          items: [
            "Credit societies",
            "Agricultural and allied sector cooperatives",
            "Marketing and consumer cooperatives",
            "Housing and urban cooperatives",
            "Labour and workers cooperatives",
            "Industrial and service cooperatives",
            "Any other society that aligns with cooperative objectives",
          ]
        }
      ],
      clickToView: "Click to view details",
      close: "Close"
    },
    ta: {
      cards: [
        {
          id: 'who-can-apply',
          title: 'யார் விண்ணப்பிக்கலாம்',
          content: "பொதுவான பொருளாதார அல்லது சமூக நோக்கம் கொண்ட எந்தவொரு தனிநபர் குழுவும் கூட்டுறவு சங்கத்தை உருவாக்க விண்ணப்பிக்கலாம். முன்மொழியப்பட்ட சங்கம் கூட்டுறவு கொள்கைகளின் அடிப்படையில் செயல்பட வேண்டும் மற்றும் அதன் உறுப்பினர்களின் கூட்டு நலனுக்காக சேவை செய்ய வேண்டும்."
        },
        {
          id: 'eligibility',
          title: 'பதிவுக்கு தகுதியான சங்கங்களின் வகைகள்',
          items: [
            "கடன் சங்கங்கள்",
            "விவசாயம் மற்றும் தொடர்புடைய துறை கூட்டுறவு நிறுவனங்கள்",
            "சந்தைப்படுத்தல் மற்றும் நுகர்வோர் கூட்டுறவு நிறுவனங்கள்",
            "வீட்டு வசதி மற்றும் நகர்ப்புற கூட்டுறவு நிறுவனங்கள்",
            "தொழிலாளர் மற்றும் பணியாளர் கூட்டுறவு நிறுவனங்கள்",
            "தொழில்துறை மற்றும் சேவை கூட்டுறவு நிறுவனங்கள்",
            "கூட்டுறவு நோக்கங்களுடன் ஒத்துப்போகும் வேறு எந்த சங்கமும்",
          ]
        }
      ],
      clickToView: "விவரங்களைப் பார்க்க கிளிக் செய்யவும்",
      close: "மூடு"
    }
  };

  const t = content[language];

  return (
    <div className="max-w-7xl mx-auto mb-14">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {t.cards.map((card, index) => {
          const colors = [
            'bg-blue-600 border-blue-600 text-white',
            'bg-gray-600 border-gray-600 text-white'
          ];

          return (
            <div
              key={card.id}
              onClick={() => setOpenModal(card.id)}
              className={`${colors[index]} rounded-2xl shadow-lg border-2 p-8 cursor-pointer transition-all hover:shadow-xl hover:-translate-y-1`}
            >
              <h2 className="text-xl font-bold text-white">
                {card.title}
              </h2>
              <p className="text-sm text-white/80 mt-2">
                {t.clickToView}
              </p>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                {t.cards.find(c => c.id === openModal)?.title}
              </h2>
              <button
                onClick={() => setOpenModal(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {openModal === 'who-can-apply' ? (
                <p className="text-gray-700 leading-relaxed">
                  {t.cards[0].content}
                </p>
              ) : (
                <ul className="space-y-3 text-gray-700">
                  {t.cards[1].items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="text-blue-600 w-5 h-5 mt-1 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
              <button
                onClick={() => setOpenModal(null)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Society() {
  const { language } = useLanguage();

  const handleRegistrationClick = () => {
    window.open('https://ercs.kooturavu.tn.gov.in/register?reset=1&type=new', '_blank', 'noopener,noreferrer');
  };

  const content = {
    en: {
      title: "Society Registration",
      overview: "Overview",
      overviewText1: "Registration of a cooperative society is the first and foundational step in forming a legally recognised cooperative institution in Tamil Nadu. The Registrar of Cooperative Societies administers the registration process in accordance with the Tamil Nadu Cooperative Societies Act, 1983, ensuring that new cooperatives are established with clear objectives, democratic governance, and statutory compliance.",
      overviewText2: "Registered cooperative societies support a wide range of activities including credit, agriculture, marketing, housing, labour, and consumer services. They play an important role in promoting inclusive and sustainable development across the State.",
      registerButton: "Click here for New Society Registration",
      
      processTitle: "Registration Process",
      processSteps: [
        "Formation of Promoter Group",
        "Preparation of Documents",
        "Submission of Application",
        "Scrutiny & Verification",
        "Clarifications (If Required)",
        "Approval of Society",
        "Certificate Issued",
        "Commencement of Operations",
        "Post-registration Compliance"
      ],
      
      documentsTitle: "Documents Required",
      documents: [
        "List of proposed members with identification details",
        "Draft bye-laws describing objectives, governance structure, capital, and operations",
        "Address proof of the registered office",
        "Details of membership subscriptions and share capital",
        "Any additional documents required for specific categories of societies",
      ]
    },
    ta: {
      title: "சங்க பதிவு",
      overview: "கண்ணோட்டம்",
      overviewText1: "கூட்டுறவு சங்க பதிவு என்பது தமிழ்நாட்டில் சட்டப்பூர்வமாக அங்கீகரிக்கப்பட்ட கூட்டுறவு நிறுவனத்தை உருவாக்குவதற்கான முதல் மற்றும் அடிப்படை படியாகும். கூட்டுறவு சங்கங்களின் பதிவாளர், தமிழ்நாடு கூட்டுறவு சங்கங்கள் சட்டம், 1983 இன் படி பதிவு செயல்முறையை நிர்வகிக்கிறார், புதிய கூட்டுறவு நிறுவனங்கள் தெளிவான நோக்கங்கள், ஜனநாயக நிர்வாகம் மற்றும் சட்டப்பூர்வ இணக்கத்துடன் நிறுவப்படுவதை உறுதி செய்கிறார்.",
      overviewText2: "பதிவு செய்யப்பட்ட கூட்டுறவு சங்கங்கள் கடன், விவசாயம், சந்தைப்படுத்தல், வீட்டுவசதி, தொழிலாளர் மற்றும் நுகர்வோர் சேவைகள் உள்ளிட்ட பரந்த அளவிலான செயல்பாடுகளை ஆதரிக்கின்றன. மாநிலம் முழுவதும் உள்ளடக்கிய மற்றும் நிலையான வளர்ச்சியை ஊக்குவிப்பதில் அவை முக்கிய பங்கு வகிக்கின்றன.",
      registerButton: "புதிய சங்க பதிவுக்கு இங்கே கிளிக் செய்யவும்",
      
      processTitle: "பதிவு செயல்முறை",
      processSteps: [
        "ஊக்குவிப்பாளர் குழு உருவாக்கம்",
        "ஆவணங்களை தயாரித்தல்",
        "விண்ணப்பத்தை சமர்ப்பித்தல்",
        "ஆய்வு மற்றும் சரிபார்ப்பு",
        "தெளிவுப்படுத்தல்கள் (தேவைப்பட்டால்)",
        "சங்க ஒப்புதல்",
        "சான்றிதழ் வழங்கப்படுதல்",
        "செயல்பாடுகளைத் தொடங்குதல்",
        "பதிவுக்குப் பிந்தைய இணக்கம்"
      ],
      
      documentsTitle: "தேவையான ஆவணங்கள்",
      documents: [
        "அடையாள விவரங்களுடன் முன்மொழியப்பட்ட உறுப்பினர்களின் பட்டியல்",
        "நோக்கங்கள், நிர்வாக அமைப்பு, மூலதனம் மற்றும் செயல்பாடுகளை விவரிக்கும் வரைவு சட்டங்கள்",
        "பதிவு செய்யப்பட்ட அலுவலகத்தின் முகவரி சான்று",
        "உறுப்பினர் சந்தாக்கள் மற்றும் பங்கு மூலதனத்தின் விவரங்கள்",
        "குறிப்பிட்ட வகை சங்கங்களுக்கு தேவையான கூடுதல் ஆவணங்கள்",
      ]
    }
  };

  const t = content[language];

  return (
    <div className="w-full bg-white py-12 px-4 lg:px-20">
      {/* Header */}
      <div className="max-w-7xl mx-auto text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900">
          {t.title}
        </h1>
      </div>

      {/* Overview */}
      <div className="max-w-7xl mx-auto bg-[#f1f7ff] rounded-2xl p-8 mb-14">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{t.overview}</h2>
        <div className="border-l-4 border-blue-600 pl-4 text-gray-700 leading-relaxed space-y-4">
          <p>{t.overviewText1}</p>
          <p>{t.overviewText2}</p>
        </div>
      </div>

      {/* Registration Button */}
      <div className="max-w-7xl mx-auto mb-14 text-center">
<button
  onClick={handleRegistrationClick}
  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 text-white font-semibold rounded-lg shadow-lg hover:bg-teal-600 hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 cursor-pointer"
>
  {t.registerButton}
  <ExternalLink className="w-5 h-5" />
</button>
      </div>

      {/* Who Can Apply - Card Section */}
      <EligibilityCardsSection />

      {/* Process Flow – Full Screen Width */}
      <div className="w-full bg-gray-100 mb-10 sm:mb-20 py-10 sm:py-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">
          {t.processTitle}
        </h2>

        {/* Inner content max width */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-16 relative">
            {t.processSteps.map((step, idx) => {
              const colors = [
                "bg-cyan-200", "bg-blue-300", "bg-purple-300",
                "bg-pink-300", "bg-orange-300", "bg-yellow-300",
                "bg-orange-200", "bg-yellow-200", "bg-green-300"
              ];
              
              return (
                <div
                  key={idx}
                  className={`relative rounded-2xl p-6 text-center shadow-md ${colors[idx]}`}
                >
                  {/* Step circle */}
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-white shadow rounded-full h-12 w-12 flex items-center justify-center text-lg font-bold">
                    {idx + 1}
                  </div>

                  <p className="font-semibold text-gray-800 mt-6">
                    {step}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Documents Required */}
      <div className="w-full bg-blue-100 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
            {t.documentsTitle}
          </h2>

          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-blue-900">
            {t.documents.map((doc, idx) => (
              <li
                key={idx}
                className="flex items-start gap-3 p-4 bg-white rounded-xl shadow hover:shadow-lg transition-shadow duration-200"
              >
                <CheckCircle className="text-blue-600 w-6 h-6 mt-1 flex-shrink-0" />
                <span className="leading-snug text-sm md:text-base">{doc}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}